import { create } from 'zustand';
import sliderService from '../api/sliderService';
import logger from '../utils/logger';

// ponytail: Zustand store replacing the SliderContext boilerplate
export const useSliderStore = create((set, get) => ({
  sliderItems: [],
  loading: true,
  error: null,

  fetchSlides: async () => {
    try {
      set({ loading: true });
      const response = await sliderService.getAllSlides();
      set({ sliderItems: response.data.slides || [], error: null });
    } catch (err) {
      logger.error('Error fetching slides:', err);
      set({ error: err.response?.data?.message || 'Failed to load slides' });
    } finally {
      set({ loading: false });
    }
  },

  getActiveSlides: async () => {
    try {
      const response = await sliderService.getActiveSlides();
      return response.data.slides || [];
    } catch (err) {
      logger.error('Error fetching active slides:', err);
      return get().sliderItems.filter(s => s.isActive).sort((a, b) => a.order - b.order);
    }
  },

  addSlide: async (formData) => {
    try {
      const response = await sliderService.createSlide(formData);
      set((state) => ({ sliderItems: [...state.sliderItems, response.data.slide] }));
      return { success: true, data: response.data.slide };
    } catch (err) {
      logger.error('Error creating slide:', err);
      return { success: false, message: err.response?.data?.message || 'Failed to create slide' };
    }
  },

  updateSlide: async (slideId, formData) => {
    try {
      const response = await sliderService.updateSlide(slideId, formData);
      set((state) => ({
        sliderItems: state.sliderItems.map((s) => (s._id === slideId ? response.data.slide : s)),
      }));
      return { success: true, data: response.data.slide };
    } catch (err) {
      logger.error('Error updating slide:', err);
      return { success: false, message: err.response?.data?.message || 'Failed to update slide' };
    }
  },

  deleteSlide: async (slideId) => {
    try {
      await sliderService.deleteSlide(slideId);
      set((state) => ({ sliderItems: state.sliderItems.filter((s) => s._id !== slideId) }));
      return { success: true };
    } catch (err) {
      logger.error('Error deleting slide:', err);
      return { success: false, message: err.response?.data?.message || 'Failed to delete slide' };
    }
  },

  deleteSlides: async (slideIds) => {
    try {
      await Promise.all(slideIds.map((id) => sliderService.deleteSlide(id)));
      set((state) => ({ sliderItems: state.sliderItems.filter((s) => !slideIds.includes(s._id)) }));
      return { success: true };
    } catch (err) {
      logger.error('Error deleting slides:', err);
      return { success: false, message: err.response?.data?.message || 'Failed to delete slides' };
    }
  },

  toggleSlideActive: async (slideId) => {
    const slide = get().sliderItems.find((s) => s._id === slideId);
    if (!slide) return { success: false, message: 'Slide not found' };

    const formData = new FormData();
    formData.append('isActive', !slide.isActive);

    return get().updateSlide(slideId, formData);
  },

  moveSlide: async (slideId, direction) => {
    try {
      const items = get().sliderItems;
      const currentIndex = items.findIndex((s) => s._id === slideId);
      if (currentIndex === -1) return { success: false, message: 'Slide not found' };

      let newOrder = items[currentIndex].order;
      if (direction === 'up' && currentIndex > 0) {
        newOrder = items[currentIndex - 1].order;
      } else if (direction === 'down' && currentIndex < items.length - 1) {
        newOrder = items[currentIndex + 1].order;
      }

      const formData = new FormData();
      formData.append('order', newOrder);

      await sliderService.updateSlide(slideId, formData);
      await get().fetchSlides();
      return { success: true };
    } catch (err) {
      logger.error('Error reordering slide:', err);
      return { success: false, message: err.response?.data?.message || 'Failed to reorder slide' };
    }
  },

  getStats: () => {
    const items = get().sliderItems;
    const total = items.length;
    const active = items.filter((s) => s.isActive).length;
    return {
      total,
      active,
      inactive: total - active,
      activeRate: total > 0 ? Math.round((active / total) * 100) : 0,
    };
  },
}));

// ponytail: init driven by PublicDataLoader — not here
